<script setup>
import { computed, ref } from 'vue'
import TextWithDefinitions from './TextWithDefinitions.vue'
import { markCheckbox } from '../utils'
import { vMaska } from 'maska'
import { useQuestionsStore } from '../stores/QuestionsStore'
import { storeToRefs } from 'pinia'

const props = defineProps({
  question: { type: Object, required: true },
  questionIndex: { type: Number, default: 0 },
  showQuestionNumber: { type: Boolean, default: true },
  originalAnswer: { type: [String, Array, Object, Number], default: null },
  originalOptions: { type: Array, default: () => [] },
  warning: { type: String, default: null },
  isSaving: { type: Boolean, default: false }
})

const { questionIndex, showQuestionNumber } = props

const emit = defineEmits(['answer-changed'])
const localQuestion = computed(() => props.question)
const questionStore = useQuestionsStore()
const { erisaDate, isValidDate } = storeToRefs(questionStore)

const rawMaskVal = {}
const hasUserModified = ref(false)

const currentAnswerText = computed(() => {
  if (props.originalAnswer === null || props.originalAnswer === undefined) return null
  if (!props.originalOptions?.length) return null
  
  const questionType = props.question?.type
  const wrongAnswerIndexes = props.question?.wrongAnswer?.values || []
  
  if (questionType === 1 && Array.isArray(props.originalAnswer)) {
    // When wrong answers exist, only show the wrong selections
    const answersToShow = wrongAnswerIndexes.length > 0
      ? props.originalAnswer.filter(idx => wrongAnswerIndexes.includes(idx))
      : props.originalAnswer
    
    return answersToShow
      .map(idx => props.originalOptions[idx])
      .filter(Boolean)
      .join(', ') || null
  }
  
  if (typeof props.originalAnswer === 'number') {
    return props.originalOptions[props.originalAnswer] || null
  }
  
  return null
})

const currentAnswerArray = computed(() => {
  if (props.question?.type !== 1) return []
  if (!Array.isArray(props.originalAnswer)) return []
  const wrongAnswerIndexes = props.question?.wrongAnswer?.values || []
  const answersToShow = wrongAnswerIndexes.length > 0
    ? props.originalAnswer.filter(idx => wrongAnswerIndexes.includes(idx))
    : props.originalAnswer
  
  return answersToShow
    .map(idx => props.originalOptions[idx])
    .filter(Boolean)
})

const shouldShowCurrentAnswerChecked = computed(() => {
  if (props.isSaving || hasUserModified.value) {
    return false
  }
  
  return true
})

function handleClickCheckBox(event, question, optionIndex) {
  if (!event.target.closest('.words-for-definition')) {
    hasUserModified.value = true
    markCheckbox(question, optionIndex)
    emit('answer-changed')
  }
}

function handleRadioClick(event, question, optionIndex) {
  if (!event.target.closest('.words-for-definition')) {
    hasUserModified.value = true
    question.answer = optionIndex
    emit('answer-changed')
  }
}

function handleSelectChange() {
  hasUserModified.value = true
  emit('answer-changed')
}

function handleErisaRadioClick(event, question, optionIndex) {
  if (!event.target.closest('.words-for-definition')) {
    hasUserModified.value = true
    question.answer = optionIndex
    handleErisaChange()
  }
}

function handleErisaChange() {
  hasUserModified.value = true
  emit('answer-changed')
}
</script>

<template>
  <div>
    <span class="question-title text-16">
      <strong>
        <TextWithDefinitions
          :text="showQuestionNumber ? `Q${questionIndex + 1}: ${question.question}` : question.question"
        />
      </strong>
    </span>
    <div v-if="currentAnswerText" class="current-answer-section mt-4 mb-2">
      <p class="text-12 mb-1"><strong>Your Current Selection: </strong></p>
      
      <!-- Radio (type 0) -->
      <div v-if="question.type === 0" class="form-check form-check-inline">
        <input class="form-check-input" type="radio" :checked="shouldShowCurrentAnswerChecked" disabled />
        <label class="form-check-label text-16">{{ currentAnswerText }}</label>
      </div>
      
      <!-- Checkbox (type 1) -->
      <template v-else-if="question.type === 1">
        <div v-for="(answer, idx) in currentAnswerArray" :key="'current-' + idx" class="form-check">
          <input class="form-check-input" type="checkbox" :checked="shouldShowCurrentAnswerChecked" disabled />
          <label class="form-check-label text-16">{{ answer }}</label>
        </div>
      </template>
      
      <!-- Select (type 2) -->
      <div v-else-if="question.type === 2">
        <span class="text-16">{{ currentAnswerText }}</span>
      </div>
      
      <!-- ERISA Radio (type 4) -->
      <div v-else-if="question.type === 4" class="form-check form-check-inline">
        <input class="form-check-input" type="radio" :checked="shouldShowCurrentAnswerChecked" disabled />
        <label class="form-check-label text-16">{{ currentAnswerText }}</label>
      </div>
      <!-- Warning Box -->
      <div v-if="warning" class="warning-box mb-2">
        <i class="bi bi-exclamation-triangle warning-icon"></i>
        <span class="warning-text text-12">
          <strong>
            Your selected answer is not a valid option.<br />
            {{ warning }}.<br />
            Please select one of the answers below.
          </strong>
        </span>
      </div>
    </div>
    <p class="text-12 mb-1"><strong>Valid Options: </strong></p>
    <div
      class="mt-2 erisa-date-container"
      :class="{ selected: question.answer === 0 && question.type === 4 }"
    >
      <template v-if="question.type === 0">
        <div
          v-for="(option, optionIndex) in question.options.values"
          class="form-check form-check-inline"
          :key="optionIndex"
          @click="handleRadioClick($event, question, optionIndex)"
        >
          <input
            class="form-check-input"
            type="radio"
            :id="question.id + '' + optionIndex + 'radio'"
            :name="question.id + '_radio'"
            v-model="localQuestion.answer"
            :value="optionIndex"
            :checked="localQuestion.answer === optionIndex"
          />
          <label class="form-check-label text-16">
            <TextWithDefinitions :text="option" />
          </label>
        </div>
      </template>
      <template v-else-if="question.type === 1">
        <div
          v-for="(option, optionIndex) in question.options.values"
          class="form-check"
          :key="optionIndex"
          @click="handleClickCheckBox($event, question, optionIndex)"
        >
          <input
            class="form-check-input"
            type="checkbox"
            :id="question.id + '' + optionIndex + 'check'"
            v-model="localQuestion.answer"
            :value="optionIndex"
          />
          <label class="form-check-label text-16">
            <TextWithDefinitions :text="option" />
          </label>
        </div>
      </template>
      <div v-else-if="question.type === 2">
        <select
          name="cars"
          class="form-select"
          @change="handleSelectChange"
          v-model="localQuestion.answer"
        >
          <option
            v-for="(option, i) in question.options.values"
            :value="option"
            class="text-16"
            :key="i"
          >
            <TextWithDefinitions :text="option" />
          </option>
        </select>
      </div>
      <template v-if="question.type === 4">
        <div
          v-for="(option, optionIndex) in question.options"
          class="form-check form-check-inline"
          :key="optionIndex"
          @click="handleErisaRadioClick($event, question, optionIndex)"
        >
          <input
            class="form-check-input"
            type="radio"
            :id="question.id + '' + optionIndex + 'radio'"
            :name="question.id + '_radio'"
            v-model="localQuestion.answer"
            :value="optionIndex"
          />
          <label class="form-check-label text-16">
            <TextWithDefinitions :text="option" />

            <input
              v-if="optionIndex === 0 && (question.answer === 0 || question.answer > 10)"
              type="text"
              class="form-control text-16"
              v-model="erisaDate"
              v-maska="rawMaskVal"
              data-maska="##/##/####"
              placeholder="e.g., MM/DD/YYYY"
              :class="{ 'is-invalid': !isValidDate(erisaDate) }"
              @change="handleErisaChange"
            />
          </label>
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped lang="scss">
.form-check-input {
  border: 1px solid var(--bs-gray-600, #6c757d) !important;
}

.current-answer-section {
  .text-16 {
    color: #D88201 !important;
  }
  
  .form-check-input:disabled,
  .form-check-input[disabled] {
    opacity: 1 !important;
  }

  .form-check-input:disabled ~ .form-check-label,
  .form-check-input[disabled] ~ .form-check-label {
    opacity: 1 !important;
  }
  
  .form-check-input:checked {
    background-color: #D88201 !important;
    border-color: #D88201 !important;
  }
}

.question-title {
  flex-basis: 100%;
}

.erisa-date-container {
  .form-check-inline {
    display: inline-block;
    margin-right: 1rem;
  }

  &.selected {
    .form-check-inline:not(:first-child) {
      display: block;
      margin-right: 0;
      margin-top: 0.5rem;
    }

    .erisa-date-input {
      display: block;
      margin-top: 0.5rem;
      margin-left: 1.5rem;
    }
  }
}

.warning-box {
  background: var(--Yellow-100, #FFF3CD);
  border-radius: 4px;
  padding: 7px 10px;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 421px;
  min-height: 42px;
  margin-left: 22px;
}

.warning-icon {
  color: #D88201;
  flex-shrink: 0;
}

.warning-text {
  color: #D88201;
}
</style>
